// src/pages/departments/TourismManagement.js
import { motion } from "framer-motion";

const TourismManagement = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          src="/images/department-hero.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          className="relative text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl md:text-5xl font-extrabold mb-3">
            Department of Leisure and Tourism Management
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Promoting Excellence in Travel, Culture, and Hospitality Education
          </p>
        </motion.div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Leisure and Tourism Management</strong> at the 
            <strong> Federal Polytechnic Bida</strong> was established to prepare students 
            for careers in the dynamic and fast-growing tourism and leisure industries. 
            Recognizing tourism as a key driver of national economic development, 
            the department focuses on providing quality training that blends theory 
            with real-world applications.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            The department operates under the <strong>National Board for Technical Education (NBTE)</strong> 
            and currently runs a <strong>National Diploma (ND)</strong> program designed to 
            train students in tourism management, event planning, recreational development, 
            and customer service. Plans are underway to introduce the 
            <strong> Higher National Diploma (HND)</strong> program to deepen expertise and specialization.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Since inception, the department has been instrumental in developing 
            skilled graduates who contribute to Nigeria’s tourism, hospitality, 
            and cultural heritage sectors, both locally and internationally.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <motion.img
              src="/images/hod-profile.jpg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <motion.p
              className="text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              It is my pleasure to welcome you to the 
              <strong> Department of Leisure and Tourism Management</strong> 
              at <strong>Federal Polytechnic Bida</strong>. Our department is 
              dedicated to nurturing future leaders and innovators in the 
              tourism and leisure sectors through rigorous academic programs 
              and practical training.
            </motion.p>
            <motion.p
              className="text-gray-600 leading-relaxed mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              We provide our students with the tools to understand the global 
              tourism landscape, emphasizing sustainability, cultural diversity, 
              and community development. Our graduates emerge not only as 
              professionals but as ambassadors of hospitality and national culture.
            </motion.p>
            <motion.p
              className="text-gray-600 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              I invite all students to take advantage of the learning opportunities 
              offered in this department, as we continue to shape the tourism industry 
              through innovation, professionalism, and a passion for excellence.
            </motion.p>
            <p className="mt-4 text-blue-700 font-semibold">— Mrs. Jane Doe</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be a <strong>premier center of excellence</strong> in tourism 
              and leisure management education, producing competent graduates 
              who will drive sustainable development and promote Nigeria’s 
              cultural and hospitality heritage globally.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              To train <strong>highly skilled professionals</strong> in tourism, 
              leisure, and recreation management through innovative teaching, 
              research, and community engagement, fostering economic growth 
              and cultural preservation.
            </p>
          </div>
        </motion.section>

        {/* Career Opportunities Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Career Opportunities and Entry Requirements
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Graduates of the Department of Leisure and Tourism Management have 
            diverse opportunities in both the public and private sectors, including:
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4">
            <li>Travel and Tourism Agencies</li>
            <li>Hospitality and Hotel Management</li>
            <li>Tourism Development Corporations</li>
            <li>Event and Conference Management</li>
            <li>Airline and Cruise Services</li>
            <li>Government Tourism Ministries</li>
            <li>Teaching and Research Institutions</li>
            <li>Entrepreneurship in Travel and Recreation Services</li>
          </ul>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Entry Requirements
          </h3>
          <p className="text-gray-600 leading-relaxed">
            Candidates must possess <strong>five O’Level credits</strong> 
            including English Language, Mathematics, Geography, and any two 
            from Economics, Government, or Biology, obtained at not more than 
            two sittings. A minimum score in the <strong>UTME</strong> is required 
            for admission into the <strong>ND program</strong>, which runs for 
            two academic years (four semesters).
          </p>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["gallery1.jpg", "gallery2.jpg", "gallery3.jpg", "gallery4.jpg"].map(
              (img, i) => (
                <motion.img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.leisuretourism@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default TourismManagement;
